// src/pages/Coupon.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import { BASE_ENDPOINT } from "../helpers/Config";
import toast from "react-hot-toast";
import {
    FaSyncAlt,
    FaListAlt,
    FaCheckCircle,
    FaTimesCircle,
    FaInfoCircle,
    FaCopy,
    FaTag,
    FaPlus,
    FaGift,
    FaCalendarAlt,
    FaHashtag,
    FaRegStickyNote,
    FaCoins,
    FaChartPie,
    FaWallet,
    FaDollarSign,
    FaExclamationTriangle,
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";

/**
 * ENDPOINTS
 */
const COUPON_LIST_URL = `${BASE_ENDPOINT}/admin/coupon-list`;
const COUPON_CREATE_URL = `${BASE_ENDPOINT}/admin/create-coupon`;

function nfmt(v) {
    const num = Number(v || 0);
    return new Intl.NumberFormat("en-IN").format(num);
}
function safeStr(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}
function fmtDateTime(v) {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString("en-IN");
}
function asBoolText(v) {
    if (v === true) return "Yes";
    if (v === false) return "No";
    return "—";
}
function to01String(b) {
    return b ? "1" : "0";
}
function nowMysqlTimestamp() {
    // YYYY-MM-DD HH:mm:ss (local time)
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    const ss = pad(d.getSeconds());
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}
function dtLocalToMysql(dtLocal) {
    // "YYYY-MM-DDTHH:mm" -> "YYYY-MM-DD HH:mm:ss"
    if (!dtLocal) return "";
    const s = String(dtLocal);
    if (!s.includes("T")) return s; // already in some other format
    const [d, t] = s.split("T");
    const hhmm = t?.slice(0, 5) || "00:00";
    return `${d} ${hhmm}:00`;
}

function SkeletonLine({ w = "100%" }) {
    return (
        <div className="placeholder-glow">
            <span className="placeholder" style={{ width: w, height: 10, display: "block" }} />
        </div>
    );
}

function KpiTile({ icon, label, value, loading }) {
    return (
        <div className="col-12 col-md-4">
            <div className="card border-0 shadow-sm h-100">
                <div className="card-body d-flex align-items-center gap-3" style={{ minHeight: 92 }}>
                    <div
                        className="rounded-3 d-flex align-items-center justify-content-center"
                        style={{
                            width: 44,
                            height: 44,
                            background: "rgba(13,110,253,0.10)",
                            color: "#0d6efd",
                            flex: "0 0 auto",
                        }}
                    >
                        {icon}
                    </div>
                    <div className="flex-grow-1">
                        <div className="text-muted small">{label}</div>
                        {loading ? (
                            <div className="mt-2">
                                <SkeletonLine w="60%" />
                            </div>
                        ) : (
                            <div className="h5 fw-bold mb-0 mt-1">{value}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Panel({ title, icon, right, children }) {
    return (
        <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0 pt-3 pb-2">
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ color: "#0d6efd" }}>{icon}</span>
                        <div className="fw-semibold">{title}</div>
                    </div>
                    {right}
                </div>
            </div>
            <div className="card-body pt-2">{children}</div>
        </div>
    );
}

export default function Coupon() {
    const { logout } = useAuth();

    // LIST
    const [loadingList, setLoadingList] = useState(true);
    const [coupons, setCoupons] = useState([]);
    const [info, setInfo] = useState({
        total_coupon: 0,
        active_coupon: 0,
        deactive_coupon: 0,
        page_no: 1,
        count: 0, // server returned count (often current page size / fetched count)
        limit: 0, // server per-page limit
    });
    const [lastUpdated, setLastUpdated] = useState(null);
    const [pageNo, setPageNo] = useState(1);

    // INFO MODAL
    const [showInfo, setShowInfo] = useState(false);
    const [activeCoupon, setActiveCoupon] = useState(null);

    // CREATE
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        amount: "",
        limit: 1,
        used: 0, // NEW: Added used field
        is_withdrawlable: false,
        has_expiry: false,
        expire_date: "", // UI: datetime-local (optional)
        remark: "",
    });

    // CONFIRM MODAL (before create)
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingCreate, setPendingCreate] = useState(null); // { payload, preview }

    // CREATE SUCCESS MODAL
    const [showCreated, setShowCreated] = useState(false);
    const [createdCode, setCreatedCode] = useState("");

    const totals = useMemo(() => {
        return {
            total: Number(info?.total_coupon || 0),
            active: Number(info?.active_coupon || 0),
            deactive: Number(info?.deactive_coupon || 0),
        };
    }, [info]);

    /**
     * FIXED PAGINATION:
     * - total pages should be based on total_coupon (true total count)
     * - count in response can be "fetched count" (e.g., 20) and NOT the total count.
     */
    const totalPages = useMemo(() => {
        const totalCount =
            Number(info?.total_coupon || 0) ||
            Number(info?.count || 0) ||
            Number(coupons?.length || 0) ||
            0;

        const perPage =
            Number(info?.limit || 0) ||
            Number(info?.count || 0) ||
            Number(coupons?.length || 0) ||
            1;

        return Math.max(1, Math.ceil(totalCount / Math.max(1, perPage)));
    }, [info, coupons]);

    const fetchCoupons = useCallback(
        async ({ nextPage = 1 } = {}) => {
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Token not found. Please login again.");
                logout();
                return;
            }

            setLoadingList(true);

            try {
                const response = await axios({
                    method: "post",
                    url: COUPON_LIST_URL,
                    maxBodyLength: Infinity,
                    headers: { "Content-Type": "application/json" },
                    data: { token, page_no: nextPage },
                });

                const data = response?.data;

                if (data?.error && data?.error !== false) {
                    if (data?.error === "session expired") logout();
                    else toast.error(String(data?.error));
                    setCoupons([]);
                    return;
                }

                const list = Array.isArray(data?.coupon_list) ? data.coupon_list : [];
                const inf = data?.info || {};

                setInfo({
                    total_coupon: Number(inf?.total_coupon || 0),
                    active_coupon: Number(inf?.active_coupon || 0),
                    deactive_coupon: Number(inf?.deactive_coupon || 0),
                    page_no: Number(inf?.page_no || nextPage),
                    count: Number(inf?.count || 0),
                    limit: Number(inf?.limit || 0),
                });

                setCoupons(list);
                setPageNo(Number(inf?.page_no || nextPage));
                setLastUpdated(new Date());
            } catch (err) {
                console.log(err);
                toast.error("Failed to load coupons");
                setCoupons([]);
            } finally {
                setLoadingList(false);
            }
        },
        [logout]
    );

    useEffect(() => {
        fetchCoupons({ nextPage: 1 });
    }, [fetchCoupons]);

    const lastUpdatedText = useMemo(() => {
        if (!lastUpdated) return "—";
        return lastUpdated.toLocaleString("en-IN");
    }, [lastUpdated]);

    const onRefresh = () => fetchCoupons({ nextPage: 1 });

    const goToPage = (p) => {
        const page = Number(p);
        const maxP = Number(totalPages || 1);
        const safePage = Math.min(Math.max(1, page), maxP);
        fetchCoupons({ nextPage: safePage });
    };

    const onCopy = async (code) => {
        const c = String(code || "").trim();
        if (!c) return;
        try {
            await navigator.clipboard.writeText(c);
            toast.success("Copied");
        } catch {
            try {
                const ta = document.createElement("textarea");
                ta.value = c;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
                toast.success("Copied");
            } catch {
                toast.error("Copy failed");
            }
        }
    };

    const isExpired = (c) => {
        if (!c?.has_expiry) return false;
        if (!c?.expire_date) return false;
        const d = new Date(c.expire_date);
        if (Number.isNaN(d.getTime())) return false;
        return d.getTime() < Date.now();
    };

    const isLimitExceeded = (c) => {
        const used = Number(c?.used ?? 0);
        const limit = Number(c?.limit ?? 0);
        if (!Number.isFinite(used) || !Number.isFinite(limit)) return false;
        return used >= limit && limit > 0;
    };

    const openInfo = (c) => {
        setActiveCoupon(c);
        setShowInfo(true);
    };

    const closeInfo = () => {
        setShowInfo(false);
        setActiveCoupon(null);
    };

    const closeCreated = () => {
        setShowCreated(false);
        setCreatedCode("");
    };

    const closeConfirm = () => {
        setShowConfirm(false);
        setPendingCreate(null);
    };

    useEffect(() => {
        const anyModalOpen = showInfo || showCreated || showConfirm;
        if (!anyModalOpen) return;

        const onKey = (e) => {
            if (e.key === "Escape") {
                if (showConfirm) closeConfirm();
                if (showCreated) closeCreated();
                if (showInfo) closeInfo();
            }
        };
        window.addEventListener("keydown", onKey);

        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev || "";
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showInfo, showCreated, showConfirm]);

    // CREATE handlers
    const onChange = (key, val) => setForm((p) => ({ ...p, [key]: val }));

    const buildCreatePayloadAndPreview = () => {
        const token = localStorage.getItem("token") || "";

        const amt = Number(form.amount);
        const lim = Number(form.limit);
        const used = Number(form.used); // NEW: Get used value

        if (!token) return { error: "Token not found. Please login again." };
        if (!Number.isFinite(amt) || amt <= 0) return { error: "Amount must be greater than 0" };
        if (!Number.isFinite(lim) || lim <= 0) return { error: "Limit must be at least 1" };
        if (!Number.isFinite(used) || used < 0) return { error: "Used must be a non-negative number" }; // NEW: Validate used
        if (used > lim) return { error: "Used value cannot be greater than limit" }; // NEW: Validate used <= limit

        // resolve expire date
        const expireDateMysql = form.has_expiry
            ? (form.expire_date ? dtLocalToMysql(form.expire_date) : nowMysqlTimestamp())
            : nowMysqlTimestamp();

        const payload = {
            token,
            is_withdrawlable: to01String(!!form.is_withdrawlable),
            amount: String(form.amount),
            limit: String(Number(form.limit)),
            used: String(Number(form.used)), // NEW: Add used to payload
            has_expire: to01String(!!form.has_expiry),
            expire_date: expireDateMysql,
            remark: form.remark?.trim() ? String(form.remark).trim() : null,
        };

        const preview = {
            amount: String(form.amount),
            limit: String(Number(form.limit)),
            used: String(Number(form.used)), // NEW: Add used to preview
            is_withdrawlable: form.is_withdrawlable ? "Yes" : "No",
            has_expiry: form.has_expiry ? "Yes" : "No",
            expire_date: form.has_expiry ? expireDateMysql : "Auto (current timestamp)",
            remark: form.remark?.trim() ? form.remark.trim() : "—",
        };

        return { payload, preview };
    };

    const onCreateSubmit = (e) => {
        e.preventDefault();

        const built = buildCreatePayloadAndPreview();
        if (built?.error) {
            toast.error(built.error);
            if (built.error.toLowerCase().includes("token")) logout();
            return;
        }

        setPendingCreate(built);
        setShowConfirm(true);
    };

    const confirmCreateNow = useCallback(async () => {
        if (!pendingCreate?.payload) return;

        setCreating(true);
        try {
            const response = await axios({
                method: "post",
                url: COUPON_CREATE_URL,
                maxBodyLength: Infinity,
                headers: { "Content-Type": "application/json" },
                data: pendingCreate.payload,
            });

            const data = response?.data;

            if (data?.error && data?.error !== false) {
                if (data?.error === "session expired") logout();
                else toast.error(String(data?.error));
                return;
            }

            const code = data?.coupon_code ? String(data.coupon_code) : "";
            toast.success(data?.msg || "Coupon generated successfully");

            closeConfirm();

            if (code) {
                setCreatedCode(code);
                setShowCreated(true);
            }

            setForm({
                amount: "",
                limit: 1,
                used: 0, // NEW: Reset used field
                is_withdrawlable: false,
                has_expiry: false,
                expire_date: "",
                remark: "",
            });

            fetchCoupons({ nextPage: 1 });
        } catch (err) {
            console.log(err);
            toast.error("Failed to create coupon");
        } finally {
            setCreating(false);
        }
    }, [pendingCreate, fetchCoupons, logout]);

    const infoBadges = useMemo(() => {
        const c = activeCoupon;
        if (!c) return { statusBadge: null, metaBadges: [] };

        const limitExceeded = isLimitExceeded(c);
        const expired = isExpired(c);

        let statusBadge = null;
        if (limitExceeded) {
            statusBadge = <span className="badge text-bg-warning text-dark">Limit Exceeded</span>;
        } else if (c?.status) {
            statusBadge = <span className="badge text-bg-success">Active</span>;
        } else {
            statusBadge = <span className="badge text-bg-secondary">Deactive</span>;
        }

        const metaBadges = [];
        if (c?.has_expiry) metaBadges.push(expired ? "Expired" : "Not Expired");
        metaBadges.push(c?.is_withdrawlable ? "Withdrawable" : "Non-withdrawable");

        return { statusBadge, metaBadges };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCoupon]);

    const infoRows = useMemo(() => {
        const c = activeCoupon;
        if (!c) return [];

        const limitExceeded = isLimitExceeded(c);
        const expired = isExpired(c);

        return [
            { icon: <FaTag />, k: "Coupon Code", v: safeStr(c.coupon_code) },
            { icon: <FaCoins />, k: "Amount", v: safeStr(c.amount) },
            { icon: <FaChartPie />, k: "Limit", v: safeStr(c.limit) },
            { icon: <FaWallet />, k: "Used", v: safeStr(c.used) },
            { icon: <FaCheckCircle />, k: "Status", v: c.status ? "Active" : "Deactive" },
            { icon: <FaTimesCircle />, k: "Limit Exceeded", v: asBoolText(limitExceeded) },
            { icon: <FaDollarSign />, k: "Withdrawable", v: asBoolText(!!c.is_withdrawlable) },
            { icon: <FaCalendarAlt />, k: "Has Expiry", v: asBoolText(!!c.has_expiry) },
            ...(c.has_expiry
                ? [
                    { icon: <FaCalendarAlt />, k: "Expire Date", v: c.expire_date ? fmtDateTime(c.expire_date) : "—" },
                    { icon: <FaTimesCircle />, k: "Expired", v: asBoolText(expired) },
                ]
                : []),
            { icon: <FaRegStickyNote />, k: "Remark", v: safeStr(c.remark) },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCoupon]);

    return (
        <PageTransition>
            <style>{`
                /* Professional modal (Bootstrap-friendly; no Bootstrap JS required) */
                .xmodal-backdrop{
                    position: fixed;
                    inset: 0;
                    background: rgba(15, 23, 42, .55);
                    backdrop-filter: blur(4px);
                    z-index: 1050;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 16px;
                }
                .xmodal-shell{
                    width: min(760px, 100%);
                    max-height: 86vh;
                    background: #fff;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 28px 80px rgba(0,0,0,.35);
                    display: flex;
                    flex-direction: column;
                    border: 1px solid rgba(15, 23, 42, .10);
                }
                @media (max-width: 576px){
                    .xmodal-backdrop{
                        align-items: flex-end;
                        padding: 0;
                    }
                    .xmodal-shell{
                        width: 100%;
                        max-height: 90vh;
                        border-radius: 16px 16px 0 0;
                    }
                }

                .xmodal-hero{
                    padding: 14px 14px 12px 14px;
                    background: linear-gradient(135deg, rgba(13,110,253,.12), rgba(32,201,151,.10));
                    border-bottom: 1px solid rgba(15,23,42,.08);
                }
                .xmodal-hero-top{
                    display:flex;
                    align-items:flex-start;
                    justify-content: space-between;
                    gap: 12px;
                }
                .xmodal-title{
                    margin: 0;
                    font-weight: 900;
                    font-size: 16px;
                    letter-spacing: .2px;
                }
                .xmodal-sub{
                    margin: 3px 0 0 0;
                    font-size: 12px;
                    color: rgba(15, 23, 42, .65);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .xmodal-chip-row{
                    margin-top: 10px;
                    display:flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    align-items:center;
                }
                .chip{
                    font-size: 12px;
                    padding: 6px 10px;
                    border-radius: 999px;
                    border: 1px solid rgba(15,23,42,.10);
                    background: rgba(255,255,255,.65);
                    color: rgba(15,23,42,.80);
                }

                .xmodal-body{
                    padding: 12px 14px 14px 14px;
                    overflow: auto;
                    background: #fff;
                }

                .code-card{
                    border: 1px solid rgba(15,23,42,.10);
                    background: rgba(15,23,42,.02);
                    border-radius: 14px;
                    padding: 12px;
                    display:flex;
                    align-items:center;
                    justify-content: space-between;
                    gap: 10px;
                }
                .code-left{
                    min-width: 0;
                    display:flex;
                    align-items:center;
                    gap:10px;
                }
                .code-icon{
                    width: 40px;
                    height: 40px;
                    border-radius: 12px;
                    display:flex;
                    align-items:center;
                    justify-content:center;
                    background: rgba(13,110,253,.12);
                    color: #0d6efd;
                    flex: 0 0 auto;
                }
                .code-text{
                    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
                    font-weight: 900;
                    letter-spacing: .8px;
                    font-size: 16px;
                    color: rgba(15,23,42,.90);
                    overflow:hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    max-width: 380px;
                }
                @media (max-width: 576px){
                    .code-text{ max-width: 220px; }
                }
                .code-meta{
                    margin-top: 2px;
                    font-size: 12px;
                    color: rgba(15,23,42,.60);
                }

                .details-grid{
                    margin-top: 12px;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 10px;
                }
                @media (max-width: 768px){
                    .details-grid{ grid-template-columns: 1fr; }
                }
                .detail-card{
                    border: 1px solid rgba(15,23,42,.10);
                    border-radius: 14px;
                    padding: 12px;
                    background: #fff;
                }
                .detail-title{
                    font-weight: 800;
                    font-size: 13px;
                    color: rgba(15,23,42,.85);
                    display:flex;
                    align-items:center;
                    gap: 8px;
                    margin-bottom: 10px;
                }
                .detail-row{
                    display:flex;
                    align-items:flex-start;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 8px 0;
                    border-top: 1px dashed rgba(15,23,42,.12);
                }
                .detail-row:first-child{ border-top: 0; padding-top: 0; }
                .dk{
                    display:flex;
                    align-items:center;
                    gap: 8px;
                    color: rgba(15,23,42,.65);
                    font-size: 12px;
                    min-width: 0;
                }
                .dk span{
                    flex: 0 0 auto;
                    color: rgba(13,110,253,.95);
                }
                .dv{
                    font-size: 13px;
                    font-weight: 700;
                    color: rgba(15,23,42,.90);
                    text-align: right;
                    word-break: break-word;
                }

                .xmodal-footer{
                    border-top: 1px solid rgba(15,23,42,.08);
                    padding: 10px 14px;
                    display:flex;
                    justify-content: space-between;
                    align-items:center;
                    gap: 10px;
                    background: rgba(15,23,42,.02);
                    flex-wrap: wrap;
                }
                .hint{
                    font-size: 12px;
                    color: rgba(15,23,42,.60);
                }

                /* confirm strip */
                .confirm-strip{
                    border: 1px solid rgba(255,193,7,.35);
                    background: rgba(255,193,7,.10);
                    border-radius: 14px;
                    padding: 10px 12px;
                    display:flex;
                    gap: 10px;
                    align-items:flex-start;
                }
            `}</style>

            <div className="row g-3">
                {/* Header */}
                <div className="col-12">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div>
                            <h2 className="fw-bold mb-0">Coupon Codes</h2>
                            <div className="text-muted">
                                Create and view coupon codes • Last updated:{" "}
                                <span className="fw-semibold">{lastUpdatedText}</span>
                            </div>
                        </div>

                        <button
                            className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                            onClick={onRefresh}
                            disabled={loadingList}
                            type="button"
                        >
                            <FaSyncAlt />
                            {loadingList ? "Refreshing..." : "Refresh"}
                        </button>
                    </div>
                </div>

                {/* KPI Strip */}
                <KpiTile loading={loadingList} icon={<FaListAlt />} label="Total Coupons" value={nfmt(totals.total)} />
                <KpiTile loading={loadingList} icon={<FaCheckCircle />} label="Active" value={nfmt(totals.active)} />
                <KpiTile loading={loadingList} icon={<FaTimesCircle />} label="Deactive" value={nfmt(totals.deactive)} />

                {/* Create Form */}
                <div className="col-12">
                    <Panel
                        title="Create Coupon"
                        icon={<FaPlus />}
                        right={
                            <span className={`badge ${creating ? "text-bg-secondary" : "text-bg-primary"}`}>
                                {creating ? "Creating..." : "Ready"}
                            </span>
                        }
                    >
                        <form onSubmit={onCreateSubmit}>
                            <div className="row g-2">
                                <div className="col-12 col-md-3">
                                    <label className="form-label small text-muted mb-1">Amount</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="e.g. 300"
                                        value={form.amount}
                                        onChange={(e) => onChange("amount", e.target.value)}
                                        min="0"
                                        step="0.01"
                                        required
                                    />
                                </div>

                                <div className="col-6 col-md-3">
                                    <label className="form-label small text-muted mb-1">Limit</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="e.g. 1"
                                        value={form.limit}
                                        onChange={(e) => onChange("limit", e.target.value)}
                                        min="1"
                                        step="1"
                                        required
                                    />
                                </div>

                                {/* NEW: Used input field */}
                                <div className="col-6 col-md-3">
                                    <label className="form-label small text-muted mb-1">Used</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="e.g. 0"
                                        value={form.used}
                                        onChange={(e) => onChange("used", e.target.value)}
                                        min="0"
                                        step="1"
                                        required
                                    />
                                    <div className="form-text small">Must be ≤ limit</div>
                                </div>

                                <div className="col-6 col-md-3">
                                    <label className="form-label small text-muted mb-1">Withdrawable</label>
                                    <select
                                        className="form-select"
                                        value={form.is_withdrawlable ? "yes" : "no"}
                                        onChange={(e) => onChange("is_withdrawlable", e.target.value === "yes")}
                                    >
                                        <option value="no">No</option>
                                        <option value="yes">Yes</option>
                                    </select>
                                </div>

                                <div className="col-6 col-md-3">
                                    <label className="form-label small text-muted mb-1">Has Expiry</label>
                                    <select
                                        className="form-select"
                                        value={form.has_expiry ? "yes" : "no"}
                                        onChange={(e) => onChange("has_expiry", e.target.value === "yes")}
                                    >
                                        <option value="no">No</option>
                                        <option value="yes">Yes</option>
                                    </select>
                                </div>

                                <div className="col-6 col-md-3">
                                    <label className="form-label small text-muted mb-1">Expiry Date</label>
                                    <input
                                        type="datetime-local"
                                        className="form-control"
                                        value={form.expire_date}
                                        onChange={(e) => onChange("expire_date", e.target.value)}
                                        disabled={!form.has_expiry}
                                    />
                                </div>

                                {/* Remark full width */}
                                <div className="col-6 col-md-6">
                                    <label className="form-label small text-muted mb-1">Remark (optional)</label>
                                    <input
                                        className="form-control"
                                        placeholder="Optional remark"
                                        value={form.remark}
                                        onChange={(e) => onChange("remark", e.target.value)}
                                        maxLength={200}
                                    />
                                </div>

                                <div className="col-12 d-flex justify-content-end gap-2 mt-2">
                                    <button
                                        className="btn btn-primary d-inline-flex align-items-center gap-2"
                                        type="submit"
                                        disabled={creating}
                                    >
                                        <FaTag />
                                        {creating ? "Creating..." : "Create Coupon"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </Panel>
                </div>

                {/* List */}
                <div className="col-12">
                    <Panel
                        title="Coupon List"
                        icon={<FaListAlt />}
                        right={
                            loadingList ? (
                                <span className="badge text-bg-secondary">Loading</span>
                            ) : (
                                <span className="badge text-bg-dark">
                                    Page: {nfmt(pageNo)} / {nfmt(totalPages)}
                                </span>
                            )
                        }
                    >
                        {loadingList ? (
                            <>
                                <SkeletonLine w="100%" />
                                <div className="mt-2" />
                                <SkeletonLine w="90%" />
                                <div className="mt-2" />
                                <SkeletonLine w="95%" />
                            </>
                        ) : coupons.length === 0 ? (
                            <div className="text-muted">No coupons found.</div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-sm align-middle mb-0">
                                        <thead>
                                            <tr className="text-muted">
                                                <th>Code</th>
                                                <th>Amount</th>
                                                <th className="text-center">Limit</th>
                                                <th className="text-center">Used</th>
                                                <th className="text-center">Status</th>
                                                <th className="text-end">Info</th>
                                                <th className="text-end">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {coupons.map((c, idx) => {
                                                const code = c?.coupon_code;
                                                const limitExceeded = isLimitExceeded(c);
                                                const expired = isExpired(c);

                                                return (
                                                    <tr key={c?.coupon_id ?? idx}>
                                                        <td className="fw-semibold">{safeStr(code)}</td>
                                                        <td className="fw-semibold">{safeStr(c?.amount)}</td>
                                                        <td className="text-center">{nfmt(c?.limit)}</td>
                                                        <td className="text-center">{nfmt(c?.used)}</td>

                                                        <td className="text-center">
                                                            {limitExceeded ? (
                                                                <div>
                                                                    <span className="badge text-bg-warning text-dark">
                                                                        Limit Exceeded
                                                                    </span>
                                                                    {c?.has_expiry ? (
                                                                        <div className="text-muted small mt-1">
                                                                            {expired ? "Expired" : "Not Expired"}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            ) : c?.status ? (
                                                                <span className="badge text-bg-success">Active</span>
                                                            ) : (
                                                                <span className="badge text-bg-secondary">Deactive</span>
                                                            )}
                                                        </td>

                                                        <td className="text-end">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                                                                onClick={() => openInfo(c)}
                                                            >
                                                                <FaInfoCircle />
                                                                Info
                                                            </button>
                                                        </td>

                                                        <td className="text-end">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                                                                onClick={() => onCopy(code)}
                                                                disabled={!code}
                                                            >
                                                                <FaCopy />
                                                                Copy
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination Controls */}
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
                                    <div className="text-muted small">
                                        Total: {nfmt(info?.total_coupon)} • Per page: {nfmt(info?.limit || info?.count || coupons.length)} • This page:{" "}
                                        {nfmt(coupons.length)}
                                    </div>

                                    <div className="d-flex align-items-center gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            disabled={pageNo <= 1}
                                            onClick={() => goToPage(pageNo - 1)}
                                        >
                                            Prev
                                        </button>

                                        <div className="d-flex align-items-center gap-2">
                                            <span className="text-muted small">Page</span>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: 110 }}
                                                value={pageNo}
                                                onChange={(e) => goToPage(e.target.value)}
                                                disabled={totalPages <= 1}
                                            >
                                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                                                    <option key={p} value={p}>
                                                        {p}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            disabled={pageNo >= totalPages}
                                            onClick={() => goToPage(pageNo + 1)}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </Panel>
                </div>
            </div>

            {/* CONFIRM CREATE MODAL */}
            {showConfirm ? (
                <div
                    className="xmodal-backdrop"
                    onMouseDown={(e) => {
                        if (e.target.classList.contains("xmodal-backdrop")) closeConfirm();
                    }}
                >
                    <div className="xmodal-shell" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="xmodal-hero">
                            <div className="xmodal-hero-top">
                                <div style={{ minWidth: 0 }}>
                                    <p className="xmodal-title mb-0">Confirm Coupon Creation</p>
                                    <p className="xmodal-sub">Please verify the details before generating the coupon</p>
                                </div>

                                <button className="btn btn-sm btn-outline-dark" type="button" onClick={closeConfirm} disabled={creating}>
                                    Close
                                </button>
                            </div>

                            <div className="xmodal-chip-row">
                                <span className="chip">Withdrawable: {pendingCreate?.preview?.is_withdrawlable ?? "—"}</span>
                                <span className="chip">Has Expiry: {pendingCreate?.preview?.has_expiry ?? "—"}</span>
                            </div>
                        </div>

                        <div className="xmodal-body">
                            <div className="confirm-strip">
                                <div style={{ color: "#b58100", marginTop: 2 }}>
                                    <FaExclamationTriangle />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <div className="fw-semibold">You are about to create a new coupon</div>
                                    <div className="text-muted small">
                                        Once generated, the coupon code will be shown for copying.
                                    </div>
                                </div>
                            </div>

                            <div className="details-grid">
                                <div className="detail-card">
                                    <div className="detail-title">
                                        <FaHashtag /> Inputs
                                    </div>
                                    {[
                                        { icon: <FaCoins />, k: "Amount", v: pendingCreate?.preview?.amount },
                                        { icon: <FaChartPie />, k: "Limit", v: pendingCreate?.preview?.limit },
                                        { icon: <FaWallet />, k: "Used", v: pendingCreate?.preview?.used },
                                        { icon: <FaDollarSign />, k: "Withdrawable", v: pendingCreate?.preview?.is_withdrawlable },
                                        { icon: <FaCalendarAlt />, k: "Has Expiry", v: pendingCreate?.preview?.has_expiry },
                                        { icon: <FaCalendarAlt />, k: "Expire Date", v: pendingCreate?.preview?.expire_date },
                                    ].map((r) => (
                                        <div className="detail-row" key={r.k}>
                                            <div className="dk">
                                                <span>{r.icon}</span>
                                                {r.k}
                                            </div>
                                            <div className="dv">{safeStr(r.v)}</div>
                                        </div>
                                    ))}

                                </div>

                                <div className="detail-card">
                                    <div className="detail-title">
                                        <FaRegStickyNote /> Remark
                                    </div>

                                    <div className="text-muted small mb-2">
                                        This will be stored along with the coupon (if provided).
                                    </div>

                                    <div
                                        className="p-3 rounded-3"
                                        style={{
                                            border: "1px solid rgba(15,23,42,.10)",
                                            background: "rgba(15,23,42,.02)",
                                            minHeight: 78,
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            fontWeight: 600,
                                            color: "rgba(15,23,42,.85)",
                                        }}
                                    >
                                        {safeStr(pendingCreate?.preview?.remark)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="xmodal-footer">
                            <div className="hint">Press ESC or tap outside to close.</div>
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-dark btn-sm"
                                    onClick={closeConfirm}
                                    disabled={creating}
                                >
                                    Back
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                                    onClick={confirmCreateNow}
                                    disabled={creating}
                                >
                                    <FaTag />
                                    {creating ? "Generating..." : "Confirm & Generate"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* INFO MODAL */}
            {showInfo ? (
                <div
                    className="xmodal-backdrop"
                    onMouseDown={(e) => {
                        if (e.target.classList.contains("xmodal-backdrop")) closeInfo();
                    }}
                >
                    <div className="xmodal-shell" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="xmodal-hero">
                            <div className="xmodal-hero-top">
                                <div style={{ minWidth: 0 }}>
                                    <p className="xmodal-title mb-0">Coupon Information</p>
                                    <p className="xmodal-sub">
                                        #{safeStr(activeCoupon?.coupon_id)} • Press ESC to close
                                    </p>
                                </div>

                                <div className="d-flex align-items-center gap-2">
                                    {infoBadges.statusBadge}
                                    <button type="button" className="btn btn-sm btn-outline-dark" onClick={closeInfo}>
                                        Close
                                    </button>
                                </div>
                            </div>

                            <div className="xmodal-chip-row">
                                {(infoBadges.metaBadges || []).map((t, i) => (
                                    <span className="chip" key={i}>
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="xmodal-body">
                            <div className="code-card">
                                <div className="code-left">
                                    <div className="code-icon">
                                        <FaTag />
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div className="code-text">{safeStr(activeCoupon?.coupon_code)}</div>
                                        <div className="code-meta">Share or copy this code</div>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                                    onClick={() => onCopy(activeCoupon?.coupon_code)}
                                    disabled={!activeCoupon?.coupon_code}
                                >
                                    <FaCopy />
                                    Copy
                                </button>
                            </div>

                            <div className="details-grid">
                                <div className="detail-card">
                                    <div className="detail-title">
                                        <FaHashtag /> Summary
                                    </div>

                                    {infoRows
                                        .filter((r) =>
                                            ["Coupon Code", "Amount", "Limit", "Used", "Status", "Limit Exceeded"].includes(r.k)
                                        )
                                        .map((r) => (
                                            <div className="detail-row" key={r.k}>
                                                <div className="dk">
                                                    <span>{r.icon}</span>
                                                    {r.k}
                                                </div>
                                                <div className="dv">{safeStr(r.v)}</div>
                                            </div>
                                        ))}
                                </div>

                                <div className="detail-card">
                                    <div className="detail-title">
                                        <FaInfoCircle /> Rules & Notes
                                    </div>

                                    {infoRows
                                        .filter((r) =>
                                            ["Withdrawable", "Has Expiry", "Expire Date", "Expired", "Remark"].includes(r.k)
                                        )
                                        .map((r) => (
                                            <div className="detail-row" key={r.k}>
                                                <div className="dk">
                                                    <span>{r.icon}</span>
                                                    {r.k}
                                                </div>
                                                <div className="dv">{safeStr(r.v)}</div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>

                        <div className="xmodal-footer">
                            <div className="hint">Tip: “Limit Exceeded” means used ≥ limit.</div>
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => onCopy(activeCoupon?.coupon_code)}
                                    disabled={!activeCoupon?.coupon_code}
                                >
                                    Copy Code
                                </button>
                                <button type="button" className="btn btn-outline-dark btn-sm" onClick={closeInfo}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* CREATED COUPON MODAL */}
            {showCreated ? (
                <div
                    className="xmodal-backdrop"
                    onMouseDown={(e) => {
                        if (e.target.classList.contains("xmodal-backdrop")) closeCreated();
                    }}
                >
                    <div className="xmodal-shell" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="xmodal-hero">
                            <div className="xmodal-hero-top">
                                <div style={{ minWidth: 0 }}>
                                    <p className="xmodal-title mb-0">Coupon Generated</p>
                                    <p className="xmodal-sub">Coupon generated successfully</p>
                                </div>
                                <button className="btn btn-sm btn-outline-dark" type="button" onClick={closeCreated}>
                                    Close
                                </button>
                            </div>
                        </div>

                        <div className="xmodal-body">
                            <div className="d-flex align-items-center gap-2 mb-2" style={{ color: "#0d6efd" }}>
                                <FaGift />
                                <div className="fw-semibold">Your new coupon code</div>
                            </div>

                            <div className="code-card">
                                <div className="code-left">
                                    <div className="code-icon">
                                        <FaTag />
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div className="code-text">{safeStr(createdCode)}</div>
                                        <div className="code-meta">Copy and share with users</div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                                    onClick={() => onCopy(createdCode)}
                                    disabled={!createdCode}
                                >
                                    <FaCopy />
                                    Copy
                                </button>
                            </div>

                            <div className="text-muted small mt-3">
                                After closing, refresh the list to see the new coupon on page 1.
                            </div>
                        </div>

                        <div className="xmodal-footer">
                            <div className="hint">Press ESC or tap outside to close.</div>
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => {
                                        closeCreated();
                                        onRefresh();
                                    }}
                                >
                                    Refresh List
                                </button>
                                <button type="button" className="btn btn-outline-dark btn-sm" onClick={closeCreated}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </PageTransition>
    );
}